import { createAsyncThunk } from '@reduxjs/toolkit'
import RestClient from '../../../services/client/RestClient'
import Configuration from '../../../services/api/Configuration'
import CookiesStorageService from '../../../services/CookiesStorageService'
import BiometricAuthService from '../../../services/BiometricAuthService'
import { loadTranslationsFile } from '../languageTranslation/languageThunk'

const userAuthLoginApi = Configuration.Auth_Endpoints

// Codes @aparajita/capacitor-biometric-auth throws when the user (or the system)
// dismisses the prompt rather than failing to authenticate. Deliberately backing
// out is not an error worth showing.
const CANCELLED_CODES = ['userCancel', 'systemCancel', 'appCancel', 'userFallback']

function _isCancellation(error) {
  return CANCELLED_CODES.includes(error?.code)
}

/**
 * Enables biometric login from Settings, where the password is not at hand.
 *
 * The vault replays a real `auth/login`, so it needs the password - and asking
 * for it again is the right shape regardless: re-authenticating before storing
 * a long-lived credential is the standard guard, and it proves the person
 * holding the unlocked phone is the account owner. The login response is
 * adopted as the current session so the old one is not left behind if the
 * backend rotates tokens on sign-in.
 *
 * If user-management ever grows an endpoint that mints a device-bound token,
 * only the vault contents change - the flow below stays as it is.
 */
export const enableBiometricLoginWithPassword = createAsyncThunk(
  'userAuth/enableBiometricLoginWithPassword',
  async (args, { rejectWithValue, dispatch }) => {
    const { password, reason } = args || {}
    const email = CookiesStorageService.getUserEmail()

    if (!email || !password) {
      return rejectWithValue({ message: 'Incorrect password', severity: 'error' })
    }

    const response = await RestClient.postRequest(userAuthLoginApi.login, { email, password })

    if (!response || response.status >= 300 || !response.data?.token) {
      return rejectWithValue({ wrongPassword: true })
    }

    CookiesStorageService.setUserData(response.data)
    dispatch(loadTranslationsFile())

    try {
      await BiometricAuthService.enroll({ email, password }, reason)
      return {}
    } catch (error) {
      if (_isCancellation(error)) {
        return rejectWithValue({ cancelled: true })
      }
      return rejectWithValue({
        message: error?.message || 'Could not enable biometric login',
        severity: 'error',
      })
    }
  },
)

/**
 * Turns biometric login off by deleting the vault.
 *
 * The credentials are gone from the Keychain/Keystore afterwards, so the login
 * screen stops offering the biometric button and the account can only be
 * reached with the password again.
 */
export const disableBiometricLogin = createAsyncThunk(
  'userAuth/disableBiometricLogin',
  async (_, { rejectWithValue }) => {
    try {
      await BiometricAuthService.clear()
      return {}
    } catch (error) {
      return rejectWithValue({
        message: error?.message || 'Could not turn off biometric login',
        severity: 'error',
      })
    }
  },
)

export const enrollBiometricLogin = createAsyncThunk(
  'userAuth/enrollBiometricLogin',
  async (args, { rejectWithValue }) => {
    const { credentials, reason } = args || {}
    try {
      await BiometricAuthService.enroll(credentials, reason)
      return {}
    } catch (error) {
      // Backing out of the OS prompt is a choice, not a failure. Nothing was
      // stored (the prompt is awaited before the vault is written), so the login
      // screen simply will not offer the biometric button.
      if (_isCancellation(error)) {
        return rejectWithValue({ cancelled: true })
      }
      return rejectWithValue({
        message: error?.message || 'Could not enable biometric login',
        severity: 'error',
      })
    }
  },
)

/**
 * Signs in using the credentials held in the biometric vault.
 *
 * This replays a real `auth/login` rather than reusing a stored refresh token:
 * logging out deletes the refresh token server-side, so a token-based vault
 * could never sign the user back in after an explicit logout.
 */
export const userAuthLoginWithVault = createAsyncThunk(
  'userAuth/userAuthLoginWithVault',
  async (args, { rejectWithValue, dispatch }) => {
    const { reason } = args || {}

    let credentials
    try {
      credentials = await BiometricAuthService.authenticate(reason)
    } catch (error) {
      // Cancelled or failed at the OS prompt - stay on the password form
      // without surfacing an error, the user simply did not authenticate.
      return rejectWithValue({ cancelled: true })
    }

    try {
      const response = await RestClient.postRequest(userAuthLoginApi.login, {
        email: credentials.email,
        password: credentials.password,
      })

      if (!response || response.status >= 300 || !response.data?.token) {
        throw new Error('Saved sign-in is no longer valid, please use your password')
      }

      CookiesStorageService.setUserData(response.data)
      // Mirrors userAuthLogin: setUserData writes the language cookie from the
      // response, so the translation bundle has to be refreshed to match.
      dispatch(loadTranslationsFile())

      return response.data
    } catch (error) {
      // The stored credentials no longer work (most often the password was
      // changed), so drop the vault and let the user enroll again next time.
      await BiometricAuthService.clear()
      return rejectWithValue({
        message: error?.message || 'Session expired, please log in again',
        severity: 'error',
      })
    }
  },
)
